/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    Injectable,
    Injector,
    OnDestroy,
    StaticProvider,
    Type,
} from '@angular/core';

import { Subject } from 'rxjs';
import { CinematicService } from 'ng/modules/cinematic/services/cinematic.service';

/**
 * @description
 *     Service handling cinematic log functionality for the single entry log cinematic view.
 * @author Zhiqian Liu
 */
@Injectable({
    providedIn: 'root',
})
export class LogCinematicService implements OnDestroy {
    public logCinematicCloseSubject$ = new Subject<void>();

    constructor(
        private readonly cinematicService: CinematicService,
    ) {}

    /**
     * Open a log cinematic with the component ID, component class, and optionally, custom providers based on
     * an optional custom parent injector.
     */
    public openCinematic(
        cinematicComponentId: string,
        component: Type<Component>,
        parentInjector?: Injector,
        providers?: StaticProvider[],
    ): void {
        this.cinematicService.add({
            id: cinematicComponentId,
            component: component as Type<Component>,
            componentProps: {
                onClose: () => this.closeCinematic(cinematicComponentId),
            },
            providers,
            parentInjector,
        });
    }

    /**
     * Close the log cinematic view by component ID.
     */
    public closeCinematic(cinematicComponentId: string): void {
        this.cinematicService.remove(cinematicComponentId);
        this.logCinematicCloseSubject$.next();
    }

    /**
     * Check if the log cinematic view is open.
     */
    public isOpen(cinematicComponentId: string): boolean {
        return this.cinematicService.has(cinematicComponentId);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.logCinematicCloseSubject$.complete();
    }
}
